import { Link } from 'react-router-dom';
import { CheckCircle, Clock, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageWrapper from '@/components/PageWrapper';
import { FadeUp, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const PricingPage = () => {
  const pricingPlans = [
    {
      name: 'Pilates 1:1 Session',
      price: '$85',
      description: 'Fully personalised sessions tailored to your body, goals, and movement history.',
      duration: '50 Minutes',
      features: [
        'Personalised sessions',
        'Detail-focused coaching',
        'Flexible programming',
        'Private environment',
        'Full 50 minute session',
      ],
      featured: false,
    },
    {
      name: 'Advanced Cadillac',
      price: '$95',
      description: 'Private Cadillac Session for advanced clients with prior Pilates experience.',
      duration: '50 Minutes',
      features: [
        'Full Cadillac apparatus',
        'For experienced clients',
        'Complex spring work',
        'Advanced repertoire',
        'Full 50 minute session',
      ],
      featured: true,
      badge: 'Advanced',
    },
    {
      name: 'Two Person (2:1)',
      price: '$120',
      description: 'Personalised two-person session for pairs familiar with equipment.',
      duration: '50 Minutes',
      features: [
        'Tailored for both clients',
        'Equipment familiarity required',
        'Private environment',
        'Focused studio setting',
        'Full 50 minute session',
      ],
      featured: false,
      note: 'Both participants must complete at least 3 individual sessions prior.',
    },
  ];

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="pricing-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Investment</p>
              <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                Session <span className="italic">Pricing</span>
              </h1>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                Select a session type that fits your goals and experience level.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Pricing Cards */}
      <section data-testid="pricing-cards" className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <StaggerItem key={plan.name}>
                <div
                  data-testid={`pricing-card-${index}`}
                  className={`pricing-card h-full flex flex-col ${plan.featured ? 'featured' : ''}`}
                >
                  {plan.badge && (
                    <span className="inline-block mb-4 px-3 py-1 text-xs uppercase tracking-wider text-stone-900 border border-stone-900 self-start">
                      {plan.badge}
                    </span>
                  )}
                  
                  <h3 className="font-heading text-2xl text-stone-900">{plan.name}</h3>
                  <p className="mt-2 text-stone-500 text-sm font-light leading-relaxed">{plan.description}</p>

                  <div className="mt-6 pb-6 border-b border-stone-200">
                    <span className="font-heading text-5xl text-stone-900">{plan.price}</span>
                    <span className="text-stone-500 text-sm ml-2">/ session</span>
                    <div className="flex items-center gap-2 mt-2 text-stone-500">
                      <Clock size={14} strokeWidth={1.5} />
                      <span className="text-xs">{plan.duration}</span>
                    </div>
                  </div>

                  <ul className="mt-6 space-y-3 flex-grow">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-stone-600 text-sm font-light">
                        <CheckCircle className="flex-shrink-0 mt-0.5" size={14} strokeWidth={1.5} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {plan.note && (
                    <p className="mt-6 p-4 bg-stone-100 text-stone-500 text-xs font-light">{plan.note}</p>
                  )}

                  <Button
                    asChild
                    data-testid={`pricing-book-${index}`}
                    className={`mt-8 w-full ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    <Link to="/bookings">
                      Book Session
                      <ArrowRight className="ml-2" size={14} strokeWidth={1.5} />
                    </Link>
                  </Button>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <h2 className="font-heading text-4xl text-stone-900">Have Questions?</h2>
            <p className="mt-4 text-stone-600 font-light">Get in touch to find the right session for you.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Button asChild data-testid="pricing-cta-contact" className="btn-primary">
                <Link to="/contact">
                  Get in Touch <ArrowRight className="ml-2" size={14} strokeWidth={1.5} />
                </Link>
              </Button>
              <Button asChild variant="outline" data-testid="pricing-cta-faq" className="btn-secondary">
                <Link to="/faq">View FAQ</Link>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default PricingPage;
